/**
 * LayoutContext — lets a page opt out of AppShell's max-w-[1400px] wrapper
 * and/or hide the global Sidebar (collapsing it into a burger-overlay).
 *
 * Pages that need edge-to-edge real estate (deck-view inbox, future
 * dashboards) call `useFullWidth()` and/or `useHideSidebar()` on mount;
 * AppShell, Sidebar, and TopBar all read these flags to adapt.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

interface LayoutContextValue {
  fullWidth: boolean
  setFullWidth: (v: boolean) => void
  sidebarHidden: boolean
  setSidebarHidden: (v: boolean) => void
}

const LayoutContext = createContext<LayoutContextValue | null>(null)

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [fullWidth, setFullWidth] = useState(false)
  const [sidebarHidden, setSidebarHidden] = useState(false)
  return (
    <LayoutContext.Provider
      value={{ fullWidth, setFullWidth, sidebarHidden, setSidebarHidden }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export function useLayout() {
  const ctx = useContext(LayoutContext)
  if (!ctx) throw new Error('useLayout must be used within a LayoutProvider')
  return ctx
}

/** Convenience hook: opt into full-width while mounted. */
export function useFullWidth(active = true) {
  const { setFullWidth } = useLayout()
  useEffect(() => {
    if (!active) return
    setFullWidth(true)
    return () => setFullWidth(false)
  }, [active, setFullWidth])
}

/** Convenience hook: hide the global Sidebar while mounted (burger-overlay). */
export function useHideSidebar(active = true) {
  const { setSidebarHidden } = useLayout()
  useEffect(() => {
    if (!active) return
    setSidebarHidden(true)
    return () => setSidebarHidden(false)
  }, [active, setSidebarHidden])
}
